import { useEffect, useMemo, useState } from 'react';
import { ComboSelect } from '../../../components/ComboSelect';
import { useNotify } from '../../../components/notifications';
import { fetchPlmBatches, type PlmBatchHeader } from './plm.api';
import { PLM_VALIDATE_MODE_ENABLED } from './plmWorkflow';
import {
  batchesFor, choose, jobsFor, openState, retrieveTarget,
  type PlmRetrieveMode, type PlmRetrieveState,
} from './plmRetrieve';

const MODE_OPTIONS: { value: PlmRetrieveMode; label: string }[] = [
  { value: 'view', label: 'View' },
  { value: 'enter', label: 'Enter' },
  { value: 'validate', label: 'Validate' },
].filter(m => m.value !== 'validate' || PLM_VALIDATE_MODE_ENABLED) as { value: PlmRetrieveMode; label: string }[];

/**
 * The Retrieve bar of the PLM entry screen (G5 parity 2026-10-16): Select Mode ·
 * QC Batch ID · Job ID · Retrieve — open another batch without going back to the
 * list. The batch lists are the PLM list's own (fetchPlmBatches) and its mode
 * filters; the rules are in plmRetrieve.ts. Opening goes through the host, which
 * asks before unsaved changes are left (the shared unsaved-changes confirm).
 */
export function PlmRetrieveBar({
  batchOid, mode, onRetrieve,
}: {
  /** The batch on screen and the mode it is open in — what the bar shows first. */
  batchOid: string;
  mode: PlmRetrieveMode;
  onRetrieve: (batchOid: string, mode: PlmRetrieveMode) => void;
}) {
  const notify = useNotify();
  const [all, setAll] = useState<PlmBatchHeader[]>([]);
  const [state, setState] = useState<PlmRetrieveState>({ mode, batchOid, jobId: null });

  useEffect(() => {
    let alive = true;
    fetchPlmBatches()
      .then(rows => { if (!alive) return; setAll(rows); setState(openState(rows, mode, batchOid)); })
      .catch(() => { if (alive) setAll([]); });
    return () => { alive = false; };
  }, [batchOid, mode]);

  const batchOptions = useMemo(
    () => batchesFor(all, state.mode, state.jobId).map(b => ({ value: b.batchOid, label: b.analyticalBatchId ?? b.batchOid })),
    [all, state.mode, state.jobId]);
  const jobOptions = useMemo(() => jobsFor(all, state.mode, state.batchOid), [all, state.mode, state.batchOid]);

  const retrieve = () => {
    const target = retrieveTarget(all, state);
    if ('warning' in target) { notify.warning(target.warning); return; }
    onRetrieve(target.batchOid, state.mode);
  };

  return (
    <div className="plm-retrieve" role="search" aria-label="Retrieve another batch" data-testid="plm-retrieve">
      <div className="plm-retrieve__f">
        <span>Select Mode</span>
        <ComboSelect value={state.mode} options={MODE_OPTIONS} clearable={false} placeholder=""
          onChange={v => setState(s => choose(all, s, { mode: (v || 'enter') as PlmRetrieveMode }))} />
      </div>
      <div className="plm-retrieve__f plm-retrieve__f--wide">
        <span>QC Batch ID</span>
        <ComboSelect value={state.batchOid ?? ''} options={batchOptions} placeholder=""
          onChange={v => setState(s => choose(all, s, { batchOid: v || null }))} />
      </div>
      <div className="plm-retrieve__f">
        <span>Job ID</span>
        <ComboSelect value={state.jobId ?? ''} options={jobOptions} placeholder=""
          onChange={v => setState(s => choose(all, s, { jobId: v || null }))} />
      </div>
      <button type="button" className="ca-btn ca-btn--sm ca-btn-solid" onClick={retrieve}
        title="Open the chosen QC batch (or the only batch of the Job ID) in the chosen mode">
        <i className="ti ti-download" aria-hidden="true" /> Retrieve
      </button>
    </div>
  );
}
